<template>
  <div class="shop-feature-selector">
    <div class="overflow-y-auto border border-gray-600 rounded-lg bg-gray-800" style="height: 500px">
      <BaseElCheckboxGroup v-model="enabledFeatures" class="flex flex-col" @change="onChange">
        <div
          v-for="(value, key) in displayFeatures"
          :key="key"
          class="feature-item group"
        >
          <div class="feature-content">
            <BaseElCheckbox :label="key" />
            <div class="feature-actions">
              <el-button
                size="mini"
                type="success"
                icon="el-icon-search"
                circle
                class="query-btn opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                @click="onQueryFeature(key)"
              />
              <el-button
                size="mini"
                type="info"
                icon="el-icon-document-copy"
                circle
                class="copy-btn opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                @click="onCopyFeature(key)"
              />
              <el-button
                size="mini"
                type="danger"
                icon="el-icon-delete"
                circle
                class="delete-btn opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                @click="onDeleteFeature(key)"
              />
            </div>
          </div>
        </div>
      </BaseElCheckboxGroup>
    </div>

    <!-- 新增權限區域 -->
    <ElInputWrapper>
      <div class="add-feature-section">
        <div class="add-feature-title">新增權限</div>
        <div class="flex items-center gap-2">
          <BaseElInput
            v-model="newFeatureKey"
            clearable
            placeholder="輸入 featureKey"
            class="flex-1"
            @keyup.enter="onAddFeature"
          />
          <BaseElSelect v-model="newFeatureValue" style="width: 80px">
            <BaseElSelectOption label="開啟" :value="true" />
            <BaseElSelectOption label="關閉" :value="false" />
          </BaseElSelect>
          <BaseElButton type="primary" icon="el-icon-plus" @click="onAddFeature">新增</BaseElButton>
        </div>
      </div>
    </ElInputWrapper>

    <BaseElButton block plain @click="reset">重置</BaseElButton>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, computed, reactive } from 'vue'
import store from '@/store'
import { omitBy, filter, pick, lowerCase } from 'lodash'
import { usePermissions } from '@/use/permissions'
import ElInputWrapper from '@/components/ElInputWrapper.vue'

export default defineComponent({
  name: 'ShopFeatureSelector',
  components: { ElInputWrapper },
  props: {
    search: String,
    testRoute: Boolean,
  },
  emits: ['query-feature'],
  setup (props, { emit }) {
    const { originShopPlanFeatures, refreshRoutes } = usePermissions()

    const enabledFeatures = ref([])
    const newFeatureKey = ref('')
    const newFeatureValue = ref(true)
    const originalShopFeatures = ref({}) // 備份原始資料
    const control = reactive({
      key: null,
      value: null,
    })

    const dataSource = computed(() => {
      return originShopPlanFeatures.value
    })

    const reset = async () => {
      // 恢復原始資料
      const originalData = { ...originalShopFeatures.value }
      store.commit('permission/SET_RAW_PERMISSIONS', { key: 'shopPlanFeatures', data: originalData })

      // 更新 UI 狀態
      refresh()

      // 如果啟用路由測試，則重新整理路由
      if (props.testRoute) {
        await refreshRoutes(true)
      }

      window.$message.success('已重置為原始權限狀態')
    }

    const displayFeatures = computed(() => {
      if (!props.search) return dataSource.value
      const searchKey = lowerCase(props.search)
      const features = Object.keys(dataSource.value)
      const matchs = filter(features, (feature) => lowerCase(feature).includes(searchKey))
      return pick(dataSource.value, matchs)
    })

    const onChange = async (newValue) => {
      const features = dataSource.value
      for (const key in features) {
        if (newValue.includes(key)) features[key] = true
        else features[key] = false
      }
      store.commit('permission/SET_RAW_PERMISSIONS', {
        key: 'shopPlanFeatures', data: features,
      })
      if (props.testRoute) {
        await refreshRoutes(true)
      }
    }

    const onUpdateFeatureKey = async () => {
      if (!control.key) return
      const cloneShopFeatures = { ...dataSource.value }
      cloneShopFeatures[control.key] = control.value
      store.commit('permission/SET_RAW_PERMISSIONS', { key: 'shopPlanFeatures', data: cloneShopFeatures })
      refresh()
      if (props.testRoute) await refreshRoutes(true)
    }
    const onRemoveFeatureKey = async () => {
      if (!control.key) return
      const cloneShopFeatures = { ...dataSource.value }
      delete cloneShopFeatures[control.key]
      store.commit('permission/SET_RAW_PERMISSIONS', { key: 'shopPlanFeatures', data: cloneShopFeatures })
      refresh()
      if (props.testRoute) await refreshRoutes(true)
    }

    const refresh = () => {
      const enabled = omitBy(dataSource.value, feature => !feature)
      enabledFeatures.value = Object.keys(enabled)
    }

    const onDeleteFeature = async (featureKey) => {
      const confirmed = window.confirm(`確定要刪除權限「${featureKey}」嗎？`)

      if (confirmed) {
        const cloneShopFeatures = { ...dataSource.value }
        delete cloneShopFeatures[featureKey]
        store.commit('permission/SET_RAW_PERMISSIONS', { key: 'shopPlanFeatures', data: cloneShopFeatures })
        refresh()
        if (props.testRoute) await refreshRoutes(true)
      }
    }

    const onCopyFeature = async (featureKey) => {
      try {
        await navigator.clipboard.writeText(featureKey)
        window.$message.success(`已複製權限名稱：${featureKey}`)
      } catch (err) {
        // 如果現代的 API 失敗，使用傳統方法
        const textArea = document.createElement('textarea')
        textArea.value = featureKey
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        window.$message.success(`已複製權限名稱：${featureKey}`)
      }
    }

    const onAddFeature = async () => {
      if (!newFeatureKey.value.trim()) {
        window.$message.warning('請輸入權限名稱')
        return
      }

      const cloneShopFeatures = { ...dataSource.value }
      cloneShopFeatures[newFeatureKey.value.trim()] = newFeatureValue.value
      store.commit('permission/SET_RAW_PERMISSIONS', { key: 'shopPlanFeatures', data: cloneShopFeatures })

      // 清空輸入欄位
      newFeatureKey.value = ''
      newFeatureValue.value = true

      refresh()
      if (props.testRoute) await refreshRoutes(true)
    }

    // 查詢 featureKey 資訊
    const onQueryFeature = (featureKey) => {
      emit('query-feature', featureKey)
    }

    onMounted(() => {
      // 備份原始資料
      originalShopFeatures.value = { ...originShopPlanFeatures.value }

      // 初始化 UI 狀態
      const enabled = omitBy(originShopPlanFeatures.value, feature => !feature)
      enabledFeatures.value = Object.keys(enabled)
    })
    return {
      displayFeatures,
      enabledFeatures,
      onChange,
      reset,
      refresh,
      control,
      onUpdateFeatureKey,
      onRemoveFeatureKey,
      dataSource,
      newFeatureKey,
      newFeatureValue,
      onDeleteFeature,
      onCopyFeature,
      onAddFeature,
      onQueryFeature,
    }
  },
})
</script>

<style lang="postcss" scoped>
.shop-feature-selector {
  @apply text-white h-full flex flex-col gap-[12px] pt-[12px];
}

/* Element UI 樣式覆蓋 */
::v-deep .el-button--mini {
  @apply text-xs;
}

::v-deep .el-divider {
  @apply border-gray-600;
}

::v-deep .el-checkbox__label {
  @apply text-gray-40;
}

::v-deep .el-checkbox.is-checked .el-checkbox__label {
  @apply text-[#58f993];
}

::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
  @apply bg-[#58f993];
}

.add-feature-section {
  @apply bg-gray-800 rounded-lg p-4 border border-gray-700;
}

.add-feature-title {
  @apply text-[#58f993] text-base font-medium mb-3;
}

/* 權限項目樣式 */
.feature-item {
  @apply border-b border-gray-600;
}

.feature-item:last-child {
  border-bottom: none;
}

.feature-content {
  @apply flex items-center justify-between;
}

.feature-actions {
  @apply flex items-center gap-1;
}

.group {
  @apply transition-colors duration-200 bg-gray-800 py-1.5 px-3;
}

.group:hover {
  @apply bg-gray-700;
}

.delete-btn,
.copy-btn,
.query-btn {
  @apply transition-opacity duration-200;
}

.group:hover .delete-btn,
.group:hover .copy-btn,
.group:hover .query-btn {
  @apply opacity-100;
}
</style>
